import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Trash2,
} from 'lucide-react';
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import HistoryIcon from "@mui/icons-material/History";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WhatshotIcon from "@mui/icons-material/Whatshot";

import {
  clearRecentlyViewed,
  getRecentlyViewed,
  addRecentlyViewed,
  removeTickerfromRecentlyViewed,
} from "../../../api/api";

import { useNavigate } from "react-router-dom";
import AddToWatchlistDialog from "../../IndividualStocks/Widgets/AddToWatchlistDialog";
import { fmtNumber } from "../Portfolio/portfolioHelpers";
import { getCurrencySymbol } from "../../../utils/currency";

/* ================= HELPERS ================= */
const market = sessionStorage.getItem("selectedMarket") ?? "US";
const fmtL = (n, d = 2) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const formatMarketCap = (value, sym = "$") => {
  if (!value) return "—";
  if (value >= 1e12) return `${sym}${fmtL(value / 1e12)}T`;
  if (value >= 1e9) return `${sym}${fmtL(value / 1e9)}B`;
  if (value >= 1e6) return `${sym}${fmtL(value / 1e6)}M`;
  return `${sym}${value.toLocaleString()}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";

  // Force UTC parsing by appending Z if not present
  const utcString = iso.endsWith("Z") ? iso : `${iso}Z`;

  const date = new Date(utcString);   // parsed as UTC
  const now = new Date();

  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

// const fmtNumber = (v, d = 2) =>
//   v === null || v === undefined || isNaN(v)
//     ? "—"
//     : Number(v).toLocaleString(undefined, {
//       minimumFractionDigits: d,
//       maximumFractionDigits: d,
//     });

/* ================= COMPONENT ================= */

const RecentlyViewed = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [backupRows, setBackupRows] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDeleteTicker, setConfirmDeleteTicker] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [undoing, setUndoing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [deletingTicker, setDeletingTicker] = useState(null);

  const [openAddWL, setOpenAddWL] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const sym = getCurrencySymbol();

  /* ---------- FETCH RECENTLY VIEWED ---------- */
  const fetchRecentlyViewed = async () => {
    setLoadingList(true);
    const res = await getRecentlyViewed();
    if (res?.success) setRows(res.data || []);
    setLoadingList(false);
  };

  useEffect(() => {
    fetchRecentlyViewed();
  }, []);

  /* ---------- FREQUENT VS RECENT ---------- */
  const { frequent, recent } = useMemo(() => {
    const map = {};

    rows.forEach((r) => {
      if (!map[r.ticker]) {
        map[r.ticker] = { ...r, count: 1 };
      } else {
        map[r.ticker].count += 1;
        if (new Date(r.viewed_at) > new Date(map[r.ticker].viewed_at)) {
          map[r.ticker] = { ...r, count: map[r.ticker].count };
        }
      }
    });

    const frequent = [];
    const recent = [];

    Object.values(map).forEach((item) => {
      if (item.count > 3) frequent.push(item);
      else recent.push(item);
    });

    frequent.sort((a, b) => b.count - a.count);

    return { frequent, recent };
  }, [rows]);

  /* ---------- CLEAR ALL HISTORY ---------- */
  const handleClearHistory = async () => {
    setClearing(true);
    setConfirmOpen(false);

    setBackupRows(rows);
    setRows([]);
    setSnackbarMessage("History cleared successfully");
    setSnackbarOpen(true);

    await clearRecentlyViewed();
    setClearing(false);
  };

  const handleUndo = async () => {
    if (!backupRows.length) return;

    setUndoing(true);
    await Promise.all(backupRows.map((r) => addRecentlyViewed(r.ticker)));
    await fetchRecentlyViewed();
    setUndoing(false);
    setSnackbarOpen(false);
  };

  /* ---------- DELETE INDIVIDUAL TICKER ---------- */
  const handleDeleteTicker = async (ticker) => {
    setDeletingTicker(ticker);
    setConfirmDeleteTicker(null);

    const res = await removeTickerfromRecentlyViewed(ticker);

    if (res?.success) {
      // Remove ticker from local state
      setRows(prevRows => prevRows.filter(row => row.ticker !== ticker));
      setSnackbarMessage(`${ticker} removed from history`);
      setSnackbarOpen(true);
    } else {
      setSnackbarMessage(`Failed to remove ${ticker}`);
      setSnackbarOpen(true);
    }

    setDeletingTicker(null);
  };

  /* ---------- ADD TO WATCHLIST ---------- */
  const handleAddToWatchlist = (ticker) => {
    setSelectedTicker(ticker);
    setOpenAddWL(true);
  };

  /* ---------- EMPTY STATE ---------- */
  const EmptyState = ({ icon: Icon, title, subtitle }) => (
    <Box py={6} display="flex" flexDirection="column" alignItems="center">
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: "var(--color-surface-hover)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
        }}
      >
        <Icon sx={{ fontSize: 24, color: "var(--color-text-muted)" }} />
      </Box>
      <Typography fontSize="var(--font-size-sm)" fontWeight={600} color="var(--color-text-primary)" mb={0.5}>
        {title}
      </Typography>
      <Typography fontSize={12.5} color="var(--color-text-muted)" textAlign="center" maxWidth={300} lineHeight={1.6}>
        {subtitle}
      </Typography>
    </Box>
  );

  /* ---------- TABLE RENDER ---------- */
  const renderTable = (data, isFrequent = false) => (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: "var(--color-surface-hover)" }}>
          {[
            { label: "Ticker", align: "left" },
            { label: "Company", align: "left" },
            { label: "Price", align: "right" },
            { label: "Market Cap", align: "right" },
            { label: "Sector", align: "left" },
            { label: isFrequent ? "Views" : "Last Viewed", align: "left" },
            { label: "", align: "right" },
          ].map((h) => (
            <TableCell
              key={h.label}
              align={h.align}
              sx={{
                fontSize: "var(--font-size-base)",
                fontWeight: 700,
                color: "var(--color-text-primary)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                borderBottom: "1px solid var(--color-border-primary)",
                py: 1.25,
              }}
            >
              {h.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: "none" }}>
              <EmptyState
                icon={isFrequent ? WhatshotIcon : HistoryIcon}
                title={isFrequent ? "No frequent stocks yet" : "No recent activity"}
                subtitle={
                  isFrequent
                    ? "Stocks you view more than 3 times will appear here"
                    : "Start exploring stocks to see your viewing history"
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          data.map((row) => (
            <TableRow
              key={row.ticker}
              onMouseEnter={() => setHoveredRow(row.ticker)}
              onMouseLeave={() => setHoveredRow(null)}
              sx={{
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
                borderBottom: "1px solid var(--color-border-primary)",
                cursor: "pointer",
              }}
            >
              <TableCell
                sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}
                onClick={() => navigate(`/stocks/${row.ticker}/details`)}
              >
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography
                    fontSize="var(--font-size-base)"
                    fontWeight={700}
                    color={hoveredRow === row.ticker ? "var(--color-primary-light)" : "var(--color-primary)"}
                    sx={{ transition: "color 0.2s ease" }}
                  >
                    {row.ticker}
                  </Typography>
                  {isFrequent && (
                    <Chip
                      icon={<WhatshotIcon sx={{ fontSize: 14 }} />}
                      label={`${row.count}x`}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: "var(--font-size-sm)",
                        fontWeight: 600,
                        bgcolor: "var(--color-warning-light)",
                        color: "var(--color-warning)",
                        "& .MuiChip-icon": { color: "var(--color-warning)", ml: 0.5 },
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  )}
                </Stack>
              </TableCell>

              <TableCell
                sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}
                onClick={() => navigate(`/stocks/${row.ticker}/details`)}
              >
                <Typography
                  fontSize="var(--font-size-sm)"
                  color="var(--color-text-secondary)"
                  noWrap
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.company_name}
                </Typography>
              </TableCell>

              <TableCell
                align="right"
                sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}
                onClick={() => navigate(`/stocks/${row.ticker}/details`)}
              >
                <Typography fontSize="var(--font-size-sm)" fontWeight={600} color="var(--color-text-primary)">
                  {/* ${fmtNumber(row.current_price, 2)} */}
                  {sym}{fmtNumber(row.current_price, 2)}
                </Typography>
              </TableCell>

              <TableCell
                align="right"
                sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}
                onClick={() => navigate(`/stocks/${row.ticker}/details`)}
              >
                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">
                  {/* {formatMarketCap(row.market_cap)} */}
                  {formatMarketCap(row.market_cap, sym)}
                </Typography>
              </TableCell>

              <TableCell
                sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}
                onClick={() => navigate(`/stocks/${row.ticker}/details`)}
              >
                <Chip
                  label={row.sector_name}
                  size="small"
                  sx={{
                    height: 26,
                    fontSize: "var(--font-size-sm)",
                    bgcolor: "var(--color-surface-hover)",
                    color: "var(--color-text-secondary)",
                    fontWeight: 500,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              </TableCell>

              <TableCell
                sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}
                onClick={() => navigate(`/stocks/${row.ticker}/details`)}
              >
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: "var(--color-text-muted)" }} />
                  <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                    {/* {formatDate(row.viewed_at)} */}
                    <Tooltip
                      title={new Date(
                        row.viewed_at.endsWith("Z")
                          ? row.viewed_at
                          : `${row.viewed_at}Z`
                      ).toLocaleString()}
                      arrow
                    >
                      <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                        {formatDate(row.viewed_at)}
                      </Typography>
                    </Tooltip>
                  </Typography>
                </Stack>
              </TableCell>

              <TableCell align="right" sx={{ py: 1.5, px: 1.5, borderBottom: "none" }}>
                <Tooltip title="Remove from history" arrow placement="left">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteTicker(row.ticker);
                    }}
                    disabled={deletingTicker === row.ticker}
                    sx={{
                      bgcolor: "var(--color-surface-hover)",
                      color: "var(--color-text-secondary)",
                      width: 30,
                      height: 30,
                      mr: 1.5,
                      "&:hover": {
                        bgcolor: "var(--color-surface-active)",
                        color: "var(--color-error)",
                      },
                      "&:disabled": {
                        bgcolor: "var(--color-surface-hover)",
                        color: "var(--color-text-muted)",
                      },
                    }}
                  >
                    {deletingTicker === row.ticker ? (
                      <CircularProgress size={14} sx={{ color: "var(--color-text-secondary)" }} />
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </IconButton>
                </Tooltip>
                {market !== "ALL" &&
                  <Tooltip title="Add to watchlist" arrow placement="left">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToWatchlist(row.ticker);
                      }}
                      sx={{
                        bgcolor: "var(--color-surface-hover)",
                        color: "var(--color-text-secondary)",
                        width: 30,
                        height: 30,
                        "&:hover": {
                          bgcolor: "var(--color-surface-active)",
                          color: "var(--color-primary)",
                        },
                      }}
                    >
                      <BookmarkBorderIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                }
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  /* ================= RENDER ================= */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "var(--color-bg-primary)",
        px: { xs: 2, md: 4 },
        py: 3.5,
      }}
    >
      {/* HEADER */}
      <Box style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant='title'
          fontWeight="var(--font-weight-bold)"
          className="text-primary"
          sx={{ color: 'var(--color-text-primary)' }}
          mb={0.25}
          letterSpacing="-0.01em"
        >
          Recently Viewed
        </Typography>
        <Typography
          variant='subtitle'
          sx={{ color: 'var(--color-text-secondary)' }}
          fontWeight={400}
        >
          Track your stock research and revisit companies you've explored
        </Typography>
      </Box>

      {/* ACTION BAR */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1.5}
      >
        <Box display="flex" gap={1.5} alignItems="center">
        </Box>

        <Button
          variant="outlined"
          disabled={rows.length === 0 || loadingList}
          onClick={() => setConfirmOpen(true)}
          startIcon={<DeleteSweepIcon sx={{ fontSize: 18 }} />}
          sx={{
            bgcolor: "var(--color-surface)",
            color: "var(--color-error)",
            fontSize: 13,
            fontWeight: 600,
            textTransform: "none",
            px: 2,
            py: 0.75,
            borderRadius: 1,
            border: "1px solid var(--color-border-primary)",
            "&:hover": {
              bgcolor: "var(--color-surface-hover)",
              borderColor: "var(--color-error)",
            },
            "&:disabled": {
              color: "var(--color-text-muted)",
              borderColor: "var(--color-border-primary)",
              bgcolor: "var(--color-surface)",
            },
          }}
        >
          Clear History
        </Button>
      </Box>

      {/* FREQUENT SECTION */}
      {frequent.length > 0 && (
        <Box
          sx={{
            bgcolor: "var(--color-surface)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 1.5,
            overflow: "hidden",
            mb: 2,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              bgcolor: "var(--color-warning-light)",
              borderBottom: "1px solid var(--color-warning)",
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <WhatshotIcon sx={{ color: "var(--color-warning)", fontSize: 18 }} />
              <Typography fontSize="var(--font-size-sm)" fontWeight={700} color="var(--color-warning)" letterSpacing="0.02em">
                FREQUENTLY VIEWED
              </Typography>
              <Chip
                label={`${frequent.length}`}
                size="small"
                sx={{
                  bgcolor: "var(--color-warning-light)",
                  color: "var(--color-warning)",
                  fontWeight: 600,
                  height: 22,
                  fontSize: 11,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Stack>
          </Box>
          {renderTable(frequent, true)}
        </Box>
      )}

      {/* RECENT SECTION */}
      <Box
        sx={{
          bgcolor: "var(--color-surface)",
          border: "1px solid var(--color-border-primary)",
          borderRadius: 1.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              fontSize="var(--font-size-sm)"
              fontWeight={700}
              color="var(--color-text-primary)"
              letterSpacing="0.08em"
              textTransform="uppercase"
            >
              Recent Activity
            </Typography>
            {recent.length > 0 && (
              <Chip
                label={`${recent.length}`}
                size="small"
                sx={{
                  bgcolor: "var(--color-surface-hover)",
                  color: "var(--color-text-secondary)",
                  fontWeight: 600,
                  height: 22,
                  fontSize: 12,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
          </Stack>
        </Box>
        {loadingList ? (
          <Box py={6} display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            <CircularProgress size={32} sx={{ color: "var(--color-primary)" }} />
            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
              Loading your history...
            </Typography>
          </Box>
        ) : (
          renderTable(recent)
        )}
      </Box>

      {/* CLEAR ALL HISTORY CONFIRMATION DIALOG */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "var(--color-surface)",
            backgroundImage: "none",
            borderRadius: 1.5,
            border: "1px solid var(--color-border-primary)",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "var(--color-text-primary)",
            borderBottom: "1px solid var(--color-border-primary)",
            py: 1.5,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <DeleteSweepIcon sx={{ color: "var(--color-error)", fontSize: 20 }} />
            Clear Full History?
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1.5 }}>
          <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" lineHeight={1.6} sx={{ mt: 2 }}>
            This will remove all {rows.length} item{rows.length !== 1 ? "s" : ""} from your
            recently viewed list. You can undo this action immediately after clearing.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              color: "var(--color-text-secondary)",
              "&:hover": { bgcolor: "var(--color-surface-hover)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearHistory}
            variant="contained"
            disabled={clearing}
            sx={{
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              bgcolor: "var(--color-error)",
              "&:hover": { bgcolor: "var(--color-error)", opacity: 0.9 },
              boxShadow: "none",
              py: 0.5,
              px: 1.75,
            }}
          >
            {clearing ? <CircularProgress size={14} color="inherit" /> : "Yes, Clear All"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE INDIVIDUAL TICKER CONFIRMATION DIALOG */}
      <Dialog
        open={!!confirmDeleteTicker}
        onClose={() => setConfirmDeleteTicker(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "var(--color-surface)",
            backgroundImage: "none",
            borderRadius: 1.5,
            border: "1px solid var(--color-border-primary)",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "var(--color-text-primary)",
            borderBottom: "1px solid var(--color-border-primary)",
            py: 1.5,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Trash2 size={18} color="var(--color-error)" />
            Remove from History?
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1.5 }}>
          <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" lineHeight={1.6} sx={{ mt: 2 }}>
            Remove <span style={{ color: "var(--color-primary)", fontWeight: 600 }}>{confirmDeleteTicker}</span> from your recently viewed list?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}>
          <Button
            onClick={() => setConfirmDeleteTicker(null)}
            sx={{
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              color: "var(--color-text-secondary)",
              "&:hover": { bgcolor: "var(--color-surface-hover)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteTicker(confirmDeleteTicker)}
            variant="contained"
            sx={{
              textTransform: "none",
              fontSize: 13,
              fontWeight: 500,
              bgcolor: "var(--color-error)",
              "&:hover": { bgcolor: "var(--color-error)", opacity: 0.9 },
              boxShadow: "none",
              py: 0.5,
              px: 1.75,
            }}
          >
            Yes, Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD TO WATCHLIST DIALOG */}
      <AddToWatchlistDialog
        open={openAddWL}
        onClose={() => setOpenAddWL(false)}
        ticker={selectedTicker}
      />

      {/* SNACKBAR (for undo and delete confirmations) */}
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          sx: {
            fontSize: 13,
            bgcolor: "var(--color-surface)",
            color: "var(--color-text-primary)",
            borderRadius: 1,
            border: "1px solid var(--color-border-primary)",
            minWidth: 0,
          },
        }}
        message={
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: "var(--color-success)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                color: "#fff",
              }}
            >
              ✓
            </Box>
            {snackbarMessage}
          </Stack>
        }
        action={
          backupRows.length > 0 && snackbarMessage.includes("cleared") ? (
            <Button
              size="small"
              onClick={handleUndo}
              disabled={undoing}
              sx={{
                color: "var(--color-primary)",
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                minWidth: "auto",
              }}
            >
              {undoing ? <CircularProgress size={12} sx={{ color: "var(--color-primary)" }} /> : "Undo"}
            </Button>
          ) : null
        }
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={4000}
      />
    </Box>
  );
};

export default RecentlyViewed;